import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plane, ChevronDown, Plus, X, Check, Trash2 } from 'lucide-react';
import { api } from '../services/api';
import { useDepartRoute, routeLabel } from '../contexts/DepartRouteContext';
import AirportSelect from './AirportSelect';
import RichSelect from './RichSelect';

/**
 * Topbar route switcher for the Depart section — the counterpart to retail's
 * city switcher, in the same pill-and-dropdown language.
 *
 * The list is what THIS user follows — not every route in the system. Nothing
 * is pre-populated: a new account starts empty and picks its own trips.
 *
 * One control governs the whole section. Adding a route is a modal rather than
 * an inline form, so it can be opened from the topbar on any depart page
 * without pushing the page content around.
 */

const ACCENT = '#f97316';

export default function DepartRouteSwitcher() {
  const { routes, route, routeId, setRouteId, refresh } = useDepartRoute();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Same responsive treatment as the city switcher: on phones drop the
          label and chevron to the icon badge, and anchor the panel left so it
          opens into the screen rather than off the edge. */}
      <style>{`
        @media (max-width: 640px) {
          .depart-switcher-btn { padding: 5px 10px 5px 5px !important; gap: 6px !important; }
          .depart-switcher-label, .depart-switcher-chev { display: none !important; }
          .depart-switcher-panel { left: 0 !important; right: auto !important; }
        }
      `}</style>

      <button
        type="button"
        className="depart-switcher-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={route ? routeLabel(route) : 'Track a route'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: open
            ? 'linear-gradient(135deg, rgba(249,115,22,0.18), rgba(234,88,12,0.10))'
            : 'linear-gradient(135deg, #14142a, #0f0f23)',
          border: `1px solid ${open ? '#f9731680' : '#2a2a4a'}`,
          borderRadius: 999, padding: '7px 12px 7px 8px', color: '#fff',
          fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          whiteSpace: 'nowrap', letterSpacing: 0.2,
          transition: 'background 150ms, border-color 150ms, box-shadow 150ms',
          boxShadow: open ? '0 0 0 3px rgba(249,115,22,0.12)' : 'none',
        }}
      >
        <span style={{
          width: 28, height: 28, borderRadius: '50%', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'radial-gradient(circle at 35% 30%, #f9731633, #f9731611)',
          border: '1px solid #f9731640', flexShrink: 0,
        }}>
          <Plane size={14} style={{ color: ACCENT }} />
        </span>
        <span className="depart-switcher-label">{route ? routeLabel(route) : 'Track a route'}</span>
        <ChevronDown className="depart-switcher-chev" size={14}
                     style={{ color: '#888', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </button>

      {open && (
        <div className="depart-switcher-panel" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 250, maxHeight: 340,
          overflowY: 'auto', background: '#0f0f23', border: '1px solid #1e1e3a', borderRadius: 12,
          boxShadow: '0 10px 30px #00000070', zIndex: 9999, padding: 6,
        }} role="listbox">
          {routes.length === 0 && (
            <div style={{ color: '#777', fontSize: 12, padding: '14px 12px', lineHeight: 1.6 }}>
              You aren’t tracking any routes yet. Add the trip you’re actually planning.
            </div>
          )}

          {routes.map((r) => {
            const active = r.id === routeId;
            return (
              <div key={r.id}
                   style={{
                     display: 'flex', alignItems: 'center', gap: 4,
                     background: active ? '#f9731615' : 'transparent', borderRadius: 8,
                   }}>
                <button type="button" role="option" aria-selected={active}
                        onClick={() => { setRouteId(r.id); setOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, flex: 1,
                          background: 'transparent', border: 'none', borderRadius: 8,
                          padding: '9px 11px', cursor: 'pointer',
                          color: active ? ACCENT : '#ccc', fontSize: 12.5, fontWeight: active ? 700 : 500,
                          textAlign: 'left', fontFamily: 'inherit',
                        }}>
                  <span style={{ flex: 1 }}>{routeLabel(r)}</span>
                  <span style={{ color: '#555', fontSize: 10, fontFamily: 'monospace' }}>{r.horizon_days}d</span>
                  {active && <Check size={13} />}
                </button>
                {/* Unfollowing only drops it from this list — the route and its
                    price history survive for anyone else still tracking it. */}
                <button type="button" title="Stop tracking"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await api.unsubscribeDepart(r.id).catch(() => {});
                          await refresh();
                        }}
                        style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', padding: '8px 10px' }}>
                  <Trash2 size={12} />
                </button>
              </div>
            );
          })}

          <div style={{ borderTop: '1px solid #1e1e3a', margin: '5px 0' }} />
          <button type="button" onClick={() => { setAdding(true); setOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    background: 'transparent', border: 'none', borderRadius: 8,
                    padding: '9px 11px', cursor: 'pointer', color: ACCENT,
                    fontSize: 12.5, fontWeight: 700, textAlign: 'left', fontFamily: 'inherit',
                  }}>
            <Plus size={14} /> Track a new route
          </button>
        </div>
      )}

      {adding && (
        <AddRouteModal
          onClose={() => setAdding(false)}
          onAdded={async (created) => {
            await refresh();
            setRouteId(created.id);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Track any city pair. Codes are validated for shape only — the server owns the
 * real rules, and duplicating them here would let the two drift.
 */
function AddRouteModal({ onClose, onAdded }) {
  const [f, setF] = useState({ origin: '', destination: '', horizonDays: 30, currency: 'BHD' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    // The airport pickers are comboboxes, not native inputs, so the browser's
    // own required-field check no longer covers them.
    if (!f.origin || !f.destination) { setErr('Pick both an origin and a destination.'); return; }
    setBusy(true); setErr(null);
    try {
      const out = await api.trackDepartRoute({
        origin: f.origin, destination: f.destination,
        currency: f.currency, horizonDays: Number(f.horizonDays),
      });
      onAdded(out.route);
    } catch (e2) {
      setErr(e2.message);
    } finally { setBusy(false); }
  }

  return createPortal(
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         style={{
           position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)',
           zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
         }}>
      <form onSubmit={submit} style={{
        background: 'linear-gradient(180deg, #1a1a2e 0%, #12122a 100%)',
        border: '1px solid #2a2a4a', borderRadius: 18, width: 'min(440px, 100%)',
        maxHeight: '92dvh', overflowY: 'auto',
        boxShadow: `0 20px 60px rgba(0,0,0,0.6), 0 0 80px ${ACCENT}1f`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #2a2a4a' }}>
          <Plane size={15} style={{ color: ACCENT }} />
          <span style={{ color: ACCENT, fontSize: 13, fontWeight: 800, letterSpacing: 1, flex: 1 }}>TRACK A NEW ROUTE</span>
          <button type="button" onClick={onClose}
                  style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}
                  aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {err && <div style={s.err}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, position: 'relative' }}>
            <Field label="From">
              <AirportSelect value={f.origin} onChange={(code) => setF((p) => ({ ...p, origin: code }))}
                             placeholder="City or code" autoFocus />
            </Field>
            <Field label="To">
              <AirportSelect value={f.destination} onChange={(code) => setF((p) => ({ ...p, destination: code }))}
                             placeholder="City or code" />
            </Field>
            <Field label="Days out">
              <RichSelect
                value={f.horizonDays}
                onChange={(v) => setF((p) => ({ ...p, horizonDays: Number(v) }))}
                ariaLabel="Days before departure"
                options={[14, 21, 30, 45, 60, 90].map((n) => ({ value: n, label: `${n} days` }))}
              />
            </Field>
            <Field label="Currency">
              <RichSelect
                value={f.currency}
                onChange={(v) => setF((p) => ({ ...p, currency: v }))}
                ariaLabel="Currency"
                options={['BHD', 'AED', 'OMR', 'SAR', 'KWD', 'QAR', 'INR', 'USD'].map((c) => ({ value: c, label: c }))}
              />
            </Field>
          </div>

          <p style={s.hint}>
            Any airport pair. A brand-new route needs about 25 days of observations before it can
            publish a signal.
          </p>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="submit" disabled={busy} style={s.primary}>{busy ? 'Adding…' : 'Track route'}</button>
            <button type="button" onClick={onClose} style={s.ghost}>Cancel</button>
          </div>
        </div>
      </form>
    </div>,
    document.body,
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const s = {
  input: { width: '100%', background: '#0f0f23', border: '1px solid #1e1e3a', borderRadius: 9, color: '#ccc', padding: '9px 11px', fontSize: 13, fontFamily: 'inherit' },
  hint: { color: '#666', fontSize: 11, lineHeight: 1.6, margin: '14px 0 0' },
  err: { background: 'rgba(239,68,68,0.12)', color: '#ef4444', padding: '9px 12px', borderRadius: 9, fontSize: 12, marginBottom: 12 },
  primary: { flex: 1, background: ACCENT, color: '#1a1204', border: 'none', borderRadius: 9, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  ghost: { background: 'transparent', color: '#888', border: '1px solid #333', borderRadius: 9, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
};
